import type { ReactNode } from 'react';
import { serializePartyRoutes, type PartyRoute } from './parsePartyRoutes';

export type IdentifyPartyPicker = (
  id: string | undefined,
  onSelect: (next: string | undefined) => void,
) => ReactNode;

export type IdentifyPartyEntityName = (props: {
  id: string | undefined;
  children: (name: string | undefined) => ReactNode;
}) => ReactNode;

export type IdentifyPartyRoutingSectionProps = {
  routes: PartyRoute[];
  siblingSets: Array<Set<string>>;
  entityCounts: Map<string, number>;
  reserved: Set<string>;
  slugFallback: string;
  duplicateMessage: ReactNode;
  noEntitySelectedLabel: ReactNode;
  addRouteLabel: ReactNode;
  emptyRoutesHelp: ReactNode;
  othersHelp: ReactNode;
  othersNoMatchHelp: ReactNode;
  howOutputs: ReactNode;
  routingHelp: ReactNode;
  enableOthers: boolean;
  othersEmitNullWhenNoMatch: boolean;
  renderPicker: IdentifyPartyPicker;
  EntityName: IdentifyPartyEntityName;
  onRoutesChange: (next: PartyRoute[]) => void;
  onEnableOthersChange: (checked: boolean) => void;
  onOthersEmitNullWhenNoMatchChange: (checked: boolean) => void;
};

export type IdentifyPartyConfigFormProps = {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
  headerIcon: ReactNode;
  headerTitle: ReactNode;
  headerDescription: ReactNode;
  routingHelp: ReactNode;
  addRouteLabel: ReactNode;
  emptyRoutesHelp: ReactNode;
  othersHelp: ReactNode;
  othersNoMatchHelp: ReactNode;
  howOutputs: ReactNode;
  duplicateMessage: ReactNode;
  noEntitySelectedLabel: ReactNode;
  routesConfigKey: string;
  entityIdKey: string;
  eligibleIdsKey: string;
  reservedSocketIds: readonly string[];
  slugFallback: string;
  renderPicker: IdentifyPartyPicker;
  EntityName: IdentifyPartyEntityName;
  allowlistSectionLabel: ReactNode;
  allowlistHelp: ReactNode;
  allowlistEmpty: ReactNode;
  allowlistAddLabel: string;
  llmHelp: ReactNode;
  extraBehavior?: ReactNode;
  additionalInstructionsPlaceholder: string;
};

export function readAdditionalInstructions(
  config: Record<string, unknown>,
): string {
  return typeof config.additionalInstructions === 'string'
    ? config.additionalInstructions
    : '';
}

export function patchEnableOthersRoute(
  config: Record<string, unknown>,
  checked: boolean,
): Record<string, unknown> {
  if (!checked) {
    return {
      ...config,
      enableOthersRoute: false,
      othersEmitNullWhenNoMatch: false,
    };
  }
  return { ...config, enableOthersRoute: true };
}

export function patchBooleanConfig(opts: {
  config: Record<string, unknown>;
  key: string;
  checked: boolean;
}): Record<string, unknown> {
  return { ...opts.config, [opts.key]: opts.checked };
}

export function patchAdditionalInstructions(
  config: Record<string, unknown>,
  value: string,
): Record<string, unknown> {
  return { ...config, additionalInstructions: value };
}

export function patchEligibleEntityIds(opts: {
  config: Record<string, unknown>;
  eligibleIdsKey: string;
  ids: string[];
}): Record<string, unknown> {
  return {
    ...opts.config,
    [opts.eligibleIdsKey]: opts.ids.filter((id) => id.length > 0),
  };
}

/** `null` means the replacement would duplicate another allowlist entry. */
export function replaceEligibleEntityId(opts: {
  ids: readonly string[];
  currentId: string;
  nextId: string;
}): string[] | null {
  if (opts.nextId !== opts.currentId && opts.ids.includes(opts.nextId)) {
    return null;
  }
  return opts.ids.map((id) => (id === opts.currentId ? opts.nextId : id));
}

export function patchPartyRoutes(opts: {
  config: Record<string, unknown>;
  routesConfigKey: string;
  entityIdKey: string;
  routes: PartyRoute[];
}): Record<string, unknown> {
  return {
    ...opts.config,
    [opts.routesConfigKey]: serializePartyRoutes(opts.routes, opts.entityIdKey),
  };
}

export function bindConfigPatch<T>(opts: {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
  patch: (config: Record<string, unknown>, value: T) => Record<string, unknown>;
}): (value: T) => void {
  return (value) => opts.onConfigChange(opts.patch(opts.config, value));
}

export function bindPartyRoutesChange(opts: {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
  routesConfigKey: string;
  entityIdKey: string;
}): (routes: PartyRoute[]) => void {
  return bindConfigPatch({
    config: opts.config,
    onConfigChange: opts.onConfigChange,
    patch: (current, routes) =>
      patchPartyRoutes({
        config: current,
        routesConfigKey: opts.routesConfigKey,
        entityIdKey: opts.entityIdKey,
        routes,
      }),
  });
}

export function bindEligibleIdsChange(opts: {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
  eligibleIdsKey: string;
}): (ids: string[]) => void {
  return bindConfigPatch({
    config: opts.config,
    onConfigChange: opts.onConfigChange,
    patch: (current, ids) =>
      patchEligibleEntityIds({
        config: current,
        eligibleIdsKey: opts.eligibleIdsKey,
        ids,
      }),
  });
}

export function bindNamedBooleanChange(opts: {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
  key: string;
}): (checked: boolean) => void {
  return bindConfigPatch({
    config: opts.config,
    onConfigChange: opts.onConfigChange,
    patch: (current, checked) =>
      patchBooleanConfig({ config: current, key: opts.key, checked }),
  });
}

export function appendEmptyPartyRoute(routes: PartyRoute[]): PartyRoute[] {
  return [...routes, { id: '', entityId: '', rowKey: crypto.randomUUID() }];
}

export function replacePartyRouteAt(opts: {
  routes: PartyRoute[];
  index: number;
  updated: PartyRoute;
}): PartyRoute[] {
  return opts.routes.map((current, currentIndex) =>
    currentIndex === opts.index ? opts.updated : current,
  );
}

export function removePartyRouteAt(
  routes: PartyRoute[],
  index: number,
): PartyRoute[] {
  return routes.filter((_, currentIndex) => currentIndex !== index);
}
